import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import type { TestDataProps } from "@repo/ui";
import { useTranslation } from "react-i18next";
import type { CashierTradingAccount } from "@/api/cashier";
import { AccountCard } from "@/components/account-list/AccountCard";
import { Icon } from "@/components/general/icon/Icon";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { EIcon } from "@/utils/enums";
import styles from "./AccountBalanceCard.module.css";
import type Decimal from "decimal.js";

type AccountBalanceProps = {
  isLoading?: boolean;
  account: CashierTradingAccount;
  refundAmount: Decimal;
  profitAmount: Decimal;
} & TestDataProps;

export const AccountBalanceCard = ({
  account,
  isLoading,
  refundAmount,
  profitAmount,
  "data-testid": testId,
}: AccountBalanceProps) => {
  const { t } = useTranslation();
  const { formatAmount } = useFormatAmount();

  return (
    <AccountCard
      account={account}
      className={styles.accountCard}
      hideCheckbox
      hideChildSeparatorLine
      data-testid={testId}
    >
      <div className={styles.accountBalance}>
        <div className={styles.accountBalanceItem}>
          <div className="ps-flex ps-gap-2 ps-items-center">
            <span data-testid={`${testId}-refund-text`}>
              {t("withdrawals.methods.account_balance_card.refundable_amounts")}
            </span>
            <Tooltip
              content={t(
                "withdrawals.methods.account_balance_card.refundable_amounts_text"
              )}
              position="right"
              data-testid={`${testId}-refund-tooltip`}
            >
              <Icon name={EIcon.Information} />
            </Tooltip>
          </div>
          <div>
            {isLoading ? (
              <SkeletonLoader width={12} height={5} variant="rectangular" />
            ) : (
              <div data-testid={`${testId}-refund-amount`}>
                {formatAmount(refundAmount, account.currency)}
              </div>
            )}
          </div>
        </div>
        <div className={styles.accountBalanceItem}>
          <div className="ps-flex ps-gap-2 ps-items-center">
            <span data-testid={`${testId}-profits-text`}>
              {t("withdrawals.methods.account_balance_card.profits")}
            </span>
            <Tooltip
              content={t(
                "withdrawals.methods.account_balance_card.profits_text"
              )}
              position="bottom"
              data-testid={`${testId}-profits-tooltip`}
            >
              <Icon name={EIcon.Information} />
            </Tooltip>
          </div>
          <div>
            {isLoading ? (
              <SkeletonLoader width={12} height={5} variant="rectangular" />
            ) : (
              <div data-testid={`${testId}-profit-amount`}>
                {formatAmount(profitAmount, account.currency)}
              </div>
            )}
          </div>
        </div>
      </div>
    </AccountCard>
  );
};
